import classNames from "classnames";
import { useGlobal } from "reactn";
import YellowAlert from "@ugg/shared/components/common/YellowAlert";

import { getTierColor, getLuTierColor, getTierListPickRateCutoff, getLuTier } from "@ugg/shared/utils/tier-helpers";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { useTranslation } from "react-i18next";
import { round, numberWithCommas } from "@ugg/shared/utils/math";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { RankingStatsInfo } from "@ugg/shared/api/data-parser/champions/ranking-stats";
import { ExtendedOverviewInfo } from "@ugg/shared/api/data-parser/champions/overview";

interface ChampionRankingStatsNormalProps {
  className?: string;
  colClassName?: string;
  rankingStats: RankingStatsInfo;
  queueType: string;
  layout?: "expanded" | "condensed";
}

export function ChampionRankingStatsNormal(props: ChampionRankingStatsNormalProps) {
  const { t } = useTranslation();
  const { className, rankingStats, queueType, layout = "condensed" } = props;
  const isExpanded = layout === "expanded";

  const { roleMatches, rank, total_rank, win_rate, pick_rate, ban_rate, stdevs } = rankingStats || {};

  const tierLetter = isNaN(stdevs) || !rank ? "?" : getLuTier(stdevs);
  const tierColor = getLuTierColor(stdevs);
  const championMatches = !isNaN(roleMatches) ? numberWithCommas(roleMatches) : "?";
  const winRate = !isNaN(win_rate) ? round(win_rate, 2) : "?";
  const winRateColor = typeof winRate === "number" && !isNaN(winRate) ? getTierColor(winRate) : "";
  const overallRank = rank && total_rank ? `${rank} / ${total_rank}` : "? / ?";
  const pickRate = !isNaN(pick_rate) && pick_rate !== null ? round(pick_rate, 1) : "?";
  const banRate = !isNaN(ban_rate) && ban_rate !== null ? round(ban_rate, 1) : "?";

  let showWarning = pickRate === "?" || pickRate < getTierListPickRateCutoff() || Number(championMatches) < 100;
  if (queueType === QueueTypeS.ONE_FOR_ALL) {
    showWarning = false;
  }

  const valueClassName = isExpanded
    ? "text-[20px] max-sm:text-[16px] max-xs:text-[14px] font-extrabold"
    : "text-[14px] font-extrabold";
  const labelClassName = isExpanded
    ? "text-[12px] font-medium text-accent-gray-100"
    : "text-[10px] font-medium text-accent-gray-100";
  const colClassName = classNames(
    "flex items-center justify-center whitespace-nowrap max-xs:py-[12px]",
    isExpanded ? "flex-col-reverse py-[24px]" : "flex-col py-3",
    props.colClassName,
  );

  return (
    <div
      className={classNames(
        `
        grid gap-[1px] grid-cols-6
      max-xs:grid-cols-3 max-xs:grid-rows-2
      rounded-[3px] bg-purple-500 [&>*]:bg-purple-200 overflow-hidden  "max-w-[50%]"
      `,
        className,
      )}
    >
      <div className={colClassName}>
        <div className={labelClassName}>Tier</div>
        <div className={classNames(valueClassName, tierColor)}>{tierLetter}</div>
      </div>
      <div className={colClassName}>
        <div className={classNames(labelClassName, "flex items-center")}>
          {t("Win Rate")}
          {showWarning && <YellowAlert className="ml-[3px]" label="Low sample size" />}
        </div>
        <div className={classNames(valueClassName, winRateColor)}>{`${winRate}%`}</div>
      </div>
      <div className={colClassName}>
        <div className={labelClassName}>{t("Rank")}</div>
        <div className={valueClassName}>{overallRank}</div>
      </div>
      <div className={colClassName}>
        <div className={labelClassName}>{t("Pick Rate")}</div>
        <div className={valueClassName}>{pickRate !== "?" && pickRate < 0.1 ? "< 0.1%" : `${pickRate}%`}</div>
      </div>
      <div className={colClassName}>
        <div className={labelClassName}>{t("Ban Rate")}</div>
        <div className={valueClassName}>{banRate !== "?" && banRate < 0.1 ? "-" : `${banRate}%`}</div>
      </div>
      <div className={colClassName}>
        <div className={labelClassName}>{t("Matches")}</div>
        <div className={valueClassName}>{championMatches}</div>
      </div>
    </div>
  );
}

interface ChampionRankingStatsMatchupProps {
  className?: string;
  oppId: number;
  oppChampionOverview: ExtendedOverviewInfo;
}

export function ChampionRankingStatsMatchup(props: ChampionRankingStatsMatchupProps) {
  const { getChampionImg, getChampionName, useChampionMini } = getRiotAssetsContext();
  const { className, oppChampionOverview, oppId } = props;

  const { t } = useTranslation();
  const [language] = useGlobal("language");
  const { data: championMini } = useChampionMini({ ssr: true, language });

  const { win_rate, matches } = oppChampionOverview;

  const winRate = win_rate && !isNaN(win_rate) ? round(100 - win_rate, 2) : "?";
  const winRateColor = typeof winRate === "number" && !isNaN(winRate) ? getTierColor(winRate) : "";
  const championMatches = !isNaN(matches) ? numberWithCommas(matches) : "?";
  let showWarning = championMatches === "?" || Number(championMatches) < 100;

  const valueClassName = "text-[20px] max-sm:text-[16px] max-xs:text-[14px] font-extrabold";
  const labelClassName = "text-[12px] font-medium text-accent-gray-100";
  const colClassName = "flex flex-col items-center justify-center py-[24px] max-xs:py-[12px] whitespace-nowrap";

  return (
    <div
      className={classNames(
        `
      grid grid-flow-col gap-[1px] grid-cols-[repeat(auto-fit,minmax(0,1fr))] 
      max-sm:grid-cols-[repeat(3,1fr)] 
      rounded-[3px] bg-purple-500 [&>*]:bg-purple-200 overflow-hidden
      `,
        className,
      )}
    >
      <div className={colClassName}>
        <div className={classNames(valueClassName, winRateColor)}>{`${winRate}%`}</div>
        <div className={classNames(labelClassName, "flex items-center")}>
          {t("Win Rate")}
          {showWarning && <YellowAlert className="ml-[3px]" label="Low sample size" />}
        </div>
      </div>
      <div className={colClassName}>
        <div className={valueClassName}>{championMatches}</div>
        <div className={labelClassName}>{t("Matches")}</div>
      </div>
      <div className="flex items-center justify-center text-[14px] font-bold">
        <span>VS.</span>
        <img className="ml-[12px] w-[32px]" src={getChampionImg(oppId)} />
        <span className="ml-[8px] max-xs:hidden">{getChampionName(oppId, { optionalData: championMini })}</span>
      </div>
    </div>
  );
}
